import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { Download, Loader2 } from 'lucide-react'
import type { DarCategoryRow } from './types'
import { downloadDarWorkbook, fetchDarCategories, toggleDarCategory } from './useAdminApi'
import { AdminTable, Banner, SectionHeader, Td, Th } from './ui'
import { DarPreviewView } from './DarPreviewView'

type DarTab = 'preview' | 'categories'

// Mirrors serving/dar_report.py's own SITES order/display names -- fixed order, not
// derived from the rows themselves, so a site with zero observed categories yet (star,
// wondercement, gti, jm_baxi as of this writing) still gets its own group heading rather
// than silently disappearing from this list the moment it has nothing to show.
const SITE_ORDER: { key: string; label: string }[] = [
  { key: 'mbmt', label: 'MBMT' },
  { key: 'ultratech', label: 'Ultratech' },
  { key: 'nagpur', label: 'Nagpur' },
  { key: 'star', label: 'Star Cement' },
  { key: 'wondercement', label: 'Wonder Cement' },
  { key: 'gti', label: 'GTI (Ports)' },
  { key: 'jm_baxi', label: 'JM Baxi (Ports)' },
]

/** Super-admin only -- enforced server-side too (403 on every /api/admin/dar route for
 *  anyone else), same gate UsersSection/AccessSection/HealthSection already use. The
 *  workbook itself has no filters: always current week + trailing 6 months, every site,
 *  server-side (see serving/dar_report.py). The only thing this page controls is which
 *  of a site's REAL, already-observed categories the next download includes --
 *  dar_category_master has no row, and this page has no toggle, for a category that
 *  has never actually fired. */
export function DarReportSection({ isSuperAdmin }: { isSuperAdmin: boolean }): JSX.Element {
  const [tab, setTab] = useState<DarTab>('preview')
  const [rows, setRows] = useState<DarCategoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [pending, setPending] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      setRows(await fetchDarCategories())
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load categories')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function onDownload(): Promise<void> {
    setDownloading(true)
    setDownloadError(null)
    try {
      await downloadDarWorkbook()
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Could not generate the report')
    } finally {
      setDownloading(false)
    }
  }

  async function onToggle(row: DarCategoryRow): Promise<void> {
    const key = `${row.site}:${row.category}`
    setPending((p) => new Set(p).add(key))
    const next = !row.enabled
    setRows((rs) => rs.map((r) => (r.site === row.site && r.category === row.category ? { ...r, enabled: next } : r)))
    try {
      await toggleDarCategory(row.site, row.category, next)
    } catch {
      // Revert on failure -- the server is the source of truth, not optimism.
      setRows((rs) => rs.map((r) => (r.site === row.site && r.category === row.category ? { ...r, enabled: !next } : r)))
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(key); return n })
    }
  }

  if (!isSuperAdmin) {
    return <Banner tone="error">This section is limited to the super administrator.</Banner>
  }

  const bySite = new Map<string, DarCategoryRow[]>()
  for (const r of rows) {
    const list = bySite.get(r.site) ?? []
    list.push(r)
    bySite.set(r.site, list)
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Daily Alerts Report (DAR)"
        subtitle="Every site, current week + trailing 6 months, critical alerts only."
        action={(
          <button
            type="button"
            onClick={() => void onDownload()}
            disabled={downloading}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Download DAR workbook
          </button>
        )}
      />

      <div className="flex gap-1 border-b border-slate-200">
        {([['preview', 'Preview'], ['categories', 'Categories']] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={clsx(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === id
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {downloadError ? <Banner tone="error" onDismiss={() => setDownloadError(null)}>{downloadError}</Banner> : null}

      {tab === 'preview' ? <DarPreviewView /> : null}

      {tab === 'categories' ? (
        <>
          <p className="text-sm text-slate-500">
            Toggle a category off to drop it from the next preview and download — it comes right back if it starts
            firing again and is re-observed.
          </p>
          {loadError ? <Banner tone="error" onDismiss={() => setLoadError(null)}>{loadError}</Banner> : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading categories…</p>
      ) : (
        <div className="flex flex-col gap-6">
          {SITE_ORDER.map(({ key, label }) => {
            const siteRows = (bySite.get(key) ?? []).sort((a, b) => a.category.localeCompare(b.category))
            return (
              <div key={key}>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</h3>
                {siteRows.length === 0 ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-400">
                    No alerts recorded yet — nothing to show here until this site's first real category is observed.
                  </p>
                ) : (
                  <AdminTable>
                    <thead>
                      <tr>
                        <Th>Category</Th>
                        <Th>First seen</Th>
                        <Th className="text-right">Show in DAR</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {siteRows.map((row) => {
                        const pendingKey = `${row.site}:${row.category}`
                        return (
                          <tr key={pendingKey}>
                            <Td>{row.display_label}</Td>
                            <Td mono>{new Date(row.first_seen_ts).toLocaleDateString('en-IN')}</Td>
                            <Td className="text-right">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={row.enabled}
                                  disabled={pending.has(pendingKey)}
                                  onChange={() => void onToggle(row)}
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                              </label>
                            </Td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </AdminTable>
                )}
              </div>
            )
          })}
        </div>
      )}
        </>
      ) : null}
    </div>
  )
}
